/**
 * Custom RUM event emitter for One Platform Portal (ALP-32444).
 *
 * Call sites never touch `trace.pushEvent` directly. Going through here buys
 * three things that are easy to get wrong once per component and impossible to
 * fix afterwards in the collected data:
 *
 *  1. Faro's `EventAttributes` is `Record<string, string>` — a number pushed
 *     raw is dropped by the SDK, so every value is stringified here.
 *  2. The collector parses logfmt and rewrites `-` and `:` in keys to `_`. Keys
 *     are normalised up front so the field name in MongoDB is the one the
 *     developer wrote, not a mangled variant.
 *  3. Long values blow up the Loki line. Values are truncated to a bounded
 *     length instead of silently producing unparseable log entries.
 *
 * Everything is a no-op when RUM never initialised (no API key, SSR, tests) —
 * `trace.pushEvent` already guards on the Faro instance.
 */
import { trace } from "../trace";
import type { AnalyticsAction, AnalyticsAttributes, AnalyticsModule } from "./taxonomy";

/** Bounded so one pathological attribute cannot break the whole logfmt line. */
const MAX_VALUE_LENGTH = 200;

/** Mirrors the collector's own key rewrite so keys survive the round trip. */
function normaliseKey(key: string): string {
  return key.trim().replace(/[-:\s.]+/g, "_");
}

function normaliseValue(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;

  if (typeof value === "number") {
    // NaN / Infinity stringify to unparseable tokens — drop rather than emit.
    if (!Number.isFinite(value)) return undefined;
    return String(value);
  }
  if (typeof value === "boolean") return String(value);
  if (typeof value !== "string") return undefined;

  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.length > MAX_VALUE_LENGTH ? `${trimmed.slice(0, MAX_VALUE_LENGTH)}…` : trimmed;
}

/** Exported for tests — the whole contract of this module lives in here. */
export function buildAttributes(attributes?: AnalyticsAttributes): Record<string, string> {
  const out: Record<string, string> = {};
  if (!attributes) return out;

  for (const [rawKey, rawValue] of Object.entries(attributes)) {
    const key = normaliseKey(rawKey);
    if (!key) continue;
    const value = normaliseValue(rawValue);
    if (value === undefined) continue;
    out[key] = value;
  }
  return out;
}

/**
 * Emit one custom event.
 *
 * @param module    the `event_domain` — where the user was
 * @param action    the `event_name` — what they did
 */
export function track(
  module: AnalyticsModule,
  action: AnalyticsAction,
  attributes?: AnalyticsAttributes
): void {
  try {
    trace.pushEvent(action, buildAttributes(attributes), module);
  } catch {
    // Telemetry must never take a user flow down with it.
  }
}

/**
 * Bind a module once so components emit with a single short call:
 *
 *   const track = analytics(AnalyticsModule.LclBooking)
 *   track(AnalyticsAction.SearchApply, { count: rows.length })
 *
 * Verbose call sites are the reason instrumentation rots — keep this cheap.
 */
export function analytics(module: AnalyticsModule) {
  return (action: AnalyticsAction, attributes?: AnalyticsAttributes) =>
    track(module, action, attributes);
}

/**
 * Time an async action and emit the event once it settles, with `result` and
 * `duration_ms` filled in. Errors are re-thrown untouched — this observes a
 * flow, it does not own its error handling.
 */
export async function trackTimed<T>(
  module: AnalyticsModule,
  action: AnalyticsAction,
  fn: () => Promise<T>,
  attributes?: AnalyticsAttributes
): Promise<T> {
  const startedAt = Date.now();
  try {
    const result = await fn();
    track(module, action, {
      ...attributes,
      result: "success",
      duration_ms: Date.now() - startedAt,
    });
    return result;
  } catch (error) {
    track(module, action, {
      ...attributes,
      result: "failure",
      duration_ms: Date.now() - startedAt,
      error_name: error instanceof Error ? error.name : "UnknownError",
      error_message: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
}
